import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Collapse,
} from "@mui/material";
import { getClientLogsById } from "../API/clientLogsAPI";

function ClientLogs({ clientId }) {
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (showLogs && clientId) {
      fetchLogs();
    }
  }, [showLogs, clientId]);

  const fetchLogs = async () => {
    if (!clientId) {
      setError("No client ID provided");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      console.log("Fetching logs for client:", clientId);
      const fetchedLogs = await getClientLogsById(clientId);
      console.log("Fetched logs:", fetchedLogs);
      setLogs(Array.isArray(fetchedLogs) ? fetchedLogs : []);
    } catch (err) {
      console.error("Error fetching client logs:", err);
      
      // Check if it's a network error (endpoint doesn't exist)
      if (err.message.includes("Failed to fetch") || err.message.includes("NetworkError")) {
        setError("Backend not configured. Please deploy the Lambda functions first (see lambda/README_CLIENT_LOGS.md)");
      } else if (err.message.includes("404")) {
        setError("API endpoint not found. Please configure API Gateway.");
      } else {
        setError(err.message || "Failed to load logs");
      }
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleLogs = () => {
    setShowLogs(!showLogs);
  };

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 1 }}>
            <Typography variant="subtitle1">Client Logs</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleToggleLogs}
            >
              {showLogs ? "Hide Logs" : "Show Logs"}
            </Button>
          </Box>
        }
        sx={{ pb: 1 }}
      />
      <Collapse in={showLogs}>
        <CardContent sx={{ px: 2, py: 1 }}>
          {isLoading && <Typography>Loading logs...</Typography>}
          {error && <Typography color="error">{error}</Typography>}
          {!isLoading && !error && logs.length === 0 && (
            <Typography variant="body2" color="textSecondary">No logs available.</Typography>
          )}
          {!isLoading && !error && logs.length > 0 && (
            <Box sx={{ width: "100%" }}>
              {/* Header Row */}
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  fontWeight: "bold",
                  borderBottom: "1px solid #e0e0e0",
                  pb: 1,
                  px: 2,
                }}
              >
                <Box sx={{ width: "10%", textAlign: "left" }}>USER</Box>
                <Box sx={{ width: "15%", textAlign: "left" }}>TIMESTAMP</Box>
                <Box sx={{ width: "37.5%", textAlign: "left" }}>PREVIOUS</Box>
                <Box sx={{ width: "37.5%", textAlign: "left" }}>UPDATE</Box>
              </Box>

              {/* Log Rows */}
              {logs.map((log, index) => {
                // UPDATED: parseLogData now supports both JSON "create" logs and diff-string logs
                const parseLogData = (logData) => {
                  if (!logData) return { previous: "N/A", updated: "N/A" };

                  // --- 1) Try JSON first (used by creation logs from postClient) ---
                  let parsedJson = null;
                  try {
                    parsedJson = JSON.parse(logData);
                  } catch (e) {
                    parsedJson = null;
                  }

                  if (parsedJson && typeof parsedJson === "object" && parsedJson.action === "create") {
                    const snap = parsedJson.client_snapshot || {};

                    const lines = [];

                    // Little helpers
                    const pushSection = (title, sectionLines) => {
                      if (!sectionLines || sectionLines.length === 0) return;
                      lines.push(`**${title}**`);
                      lines.push(...sectionLines);
                    };

                    const formatDate = (value) => {
                      if (!value) return "N/A";
                      const d = new Date(value);
                      if (Number.isNaN(d.getTime())) return String(value);
                      return d.toLocaleDateString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                      });
                    };

                    const getOwnerLabel = (ao) => {
                      if (!ao) return "None";
                      const first = (ao.firstname || "").trim();
                      const last = (ao.lastname || "").trim();
                      if (!first && !last) return "None";
                      if (first && last) return `${first} ${last}`;
                      return first || last;
                    };

                    // Optional header at the top
                    lines.push("**CLIENT CREATED**");

                    // --- CLIENT INFO ---
                    const clientInfo = [];
                    if (snap.name) clientInfo.push(`Name: "${snap.name}"`);
                    pushSection("CLIENT INFO", clientInfo);

                    // --- JOB SITE ADDRESS (first address) ---
                    const addr =
                      Array.isArray(snap.addresses) && snap.addresses[0] ? snap.addresses[0] : null;
                    const addrLines = [];
                    if (addr) {
                      if (addr.house_unit_street)
                        addrLines.push(`House/Unit/Street: "${addr.house_unit_street}"`);
                      if (addr.area) addrLines.push(`Area: "${addr.area}"`);
                      if (addr.barangay) addrLines.push(`Barangay: "${addr.barangay}"`);
                      if (addr.city) addrLines.push(`City: "${addr.city}"`);
                      if (addr.metro_province)
                        addrLines.push(`Metro/Province: "${addr.metro_province}"`);
                      if (addr.zipcode) addrLines.push(`Zipcode: "${addr.zipcode}"`);
                    }
                    pushSection("JOB SITE ADDRESS", addrLines);

                    // --- CONTACT PERSON (first contact) ---
                    const contact =
                      Array.isArray(snap.contacts) && snap.contacts[0] ? snap.contacts[0] : null;
                    const contactLines = [];
                    if (contact) {
                      if (contact.lastname) contactLines.push(`Last Name: "${contact.lastname}"`);
                      if (contact.firstname) contactLines.push(`First Name: "${contact.firstname}"`);
                      const numbers = (contact.numbers || [])
                        .map((n) => n.number)
                        .filter(Boolean);
                      if (numbers.length) {
                        contactLines.push(`Contact Numbers: "${numbers.join(", ")}"`);
                      }
                      const emails = (contact.emails || [])
                        .map((e) => e.email)
                        .filter(Boolean);
                      if (emails.length) {
                        contactLines.push(`Contact Emails: "${emails.join(", ")}"`);
                      }
                    }
                    pushSection("CONTACT PERSON", contactLines);

                    // --- CLIENT CLASSIFICATION ---
                    const classLines = [];
                    if (snap.category) classLines.push(`Category: "${snap.category}"`);
                    if (snap.location_type)
                      classLines.push(`Location Type: "${snap.location_type}"`);

                    const types = Array.isArray(snap.service_types)
                      ? snap.service_types.join(", ")
                      : snap.service_types || "";
                    if (types) classLines.push(`Service Types: "${types}"`);

                    const regular =
                      snap.regular === true || snap.regular === "true"
                        ? "Yes"
                        : snap.regular === false || snap.regular === "false"
                        ? "No"
                        : "";
                    if (regular) classLines.push(`Regular: "${regular}"`);

                    let parentName = "";
                    if (snap.parent_client) {
                      if (typeof snap.parent_client === "string") {
                        parentName = snap.parent_client;
                      } else {
                        parentName = snap.parent_client.name || "";
                      }
                    }
                    if (parentName) classLines.push(`Parent Client: "${parentName}"`);

                    classLines.push(`Account Owner: "${getOwnerLabel(snap.account_owner)}"`);

                    pushSection("CLIENT CLASSIFICATION", classLines);

                    // --- BILLING ---
                    const billingLines = [];
                    if (snap.price) billingLines.push(`Price: "${snap.price}"`);
                    if (snap.payment_terms)
                      billingLines.push(`Payment Terms: "${snap.payment_terms}"`);
                    if (snap.contract_start)
                      billingLines.push(`Contract Start: "${formatDate(snap.contract_start)}"`);
                    if (snap.contract_end)
                      billingLines.push(`Contract End: "${formatDate(snap.contract_end)}"`);

                    pushSection("BILLING", billingLines);

                    return {
                      previous: "N/A (new client)",
                      updated: lines.join("\n"),
                    };
                  }

                  // --- 2) Fallback: existing diff-string format for edit logs ---
                  const lines = logData.split("\n");
                  const previousValues = [];
                  const updatedValues = [];

                  // Helper to beautify raw keys: "location_type" → "Location Type"
                  const humanize = (key) => {
                    return key
                      .replace(/_/g, " ")
                      .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
                  };

                  lines.forEach((line) => {
                    // Section headers: **SECTION**
                    if (line.startsWith("**") && line.endsWith("**")) {
                      const header = line.slice(2, -2);
                      const humanHeader = humanize(header);
                      previousValues.push(`\n**${humanHeader}**`);
                      updatedValues.push(`\n**${humanHeader}**`);
                      return;
                    }

                    // Format: field: "old" → "new"
                    const match = line.match(/^(.+?):\s*"(.+?)"\s*→\s*"(.+?)"$/);
                    if (match) {
                      const [, fieldName, oldValue, newValue] = match;

                      const prettyField = humanize(fieldName);

                      previousValues.push(`${prettyField}: "${oldValue}"`);
                      updatedValues.push(`${prettyField}: "${newValue}"`);
                    }
                  });

                  return {
                    previous: previousValues.length > 0 ? previousValues.join("\n") : "N/A",
                    updated: updatedValues.length > 0 ? updatedValues.join("\n") : "N/A",
                  };
                };

                const { previous, updated } = parseLogData(log.log_data);

                return (
                  <Box
                    key={log.log_id || index}
                    sx={{
                      display: "flex",
                      width: "100%",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 2,
                      mt: 1,
                    }}
                  >
                    {/* User */}
                    <Box sx={{ width: "10%" }}>
                      <Typography sx={{ fontSize: "0.875rem" }}>
                        {log.user_update || ""}
                      </Typography>
                    </Box>

                    {/* Timestamp */}
                    <Box sx={{ width: "15%" }}>
                      <Typography sx={{ fontSize: "0.875rem" }}>
                        {log.timestamp_update
                          ? new Date(log.timestamp_update).toLocaleString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : ""}
                      </Typography>
                    </Box>

                    {/* Previous */}
                    <Box sx={{ width: "37.5%" }}>
                      <Box
                        component="div"
                        sx={{
                          whiteSpace: "pre-wrap",
                          fontSize: "0.875rem",
                          wordBreak: "break-word",
                        }}
                      >
                        {previous.split("\n").map((line, idx) => {
                          if (line.startsWith("**") && line.endsWith("**")) {
                            return (
                              <Box
                                key={idx}
                                sx={{
                                  fontWeight: "bold",
                                  mt: idx > 0 ? 1 : 0,
                                  mb: 0.5,
                                }}
                              >
                                {line.slice(2, -2)}
                              </Box>
                            );
                          }
                          const fieldMatch = line.match(/^(.+?):\s*(".+")$/);
                          if (fieldMatch) {
                            const [, fieldName, value] = fieldMatch;
                            return (
                              <Box key={idx}>
                                <span style={{ fontWeight: 600 }}>{fieldName}:</span>{" "}
                                {value}
                              </Box>
                            );
                          }
                          return <Box key={idx}>{line}</Box>;
                        })}
                      </Box>
                    </Box>

                    {/* Update */}
                    <Box sx={{ width: "37.5%" }}>
                      <Box
                        component="div"
                        sx={{
                          whiteSpace: "pre-wrap",
                          fontSize: "0.875rem",
                          wordBreak: "break-word",
                        }}
                      >
                        {updated.split("\n").map((line, idx) => {
                          if (line.startsWith("**") && line.endsWith("**")) {
                            return (
                              <Box
                                key={idx}
                                sx={{
                                  fontWeight: "bold",
                                  mt: idx > 0 ? 1 : 0,
                                  mb: 0.5,
                                }}
                              >
                                {line.slice(2, -2)}
                              </Box>
                            );
                          }
                          const fieldMatch = line.match(/^(.+?):\s*(".+")$/);
                          if (fieldMatch) {
                            const [, fieldName, value] = fieldMatch;
                            return (
                              <Box key={idx}>
                                <span style={{ fontWeight: 600 }}>{fieldName}:</span>{" "}
                                {value}
                              </Box>
                            );
                          }
                          return <Box key={idx}>{line}</Box>;
                        })}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
}

export default ClientLogs;
